import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeCtx = createContext(null);

export const ACCENTS = [
    { id: "olive",   label: "Olive",   ring: "#83AC39", hex: "#83AC39" },
    { id: "indigo",  label: "Indigo",  ring: "#4F46E5", hex: "#4F46E5" },
    { id: "purple",  label: "Purple",  ring: "#9333EA", hex: "#9333EA" },
    { id: "blue",    label: "Blue",    ring: "#2563EB", hex: "#2563EB" },
    { id: "rose",    label: "Rose",    ring: "#E11D48", hex: "#E11D48" },
    { id: "emerald", label: "Emerald", ring: "#059669", hex: "#059669" },
    { id: "amber",   label: "Amber",   ring: "#D97706", hex: "#D97706" },
    { id: "cyan",    label: "Cyan",    ring: "#0891B2", hex: "#0891B2" },
];

const MODES = ["light", "dark", "system"];

function systemPrefersDark() {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

function applyTheme(mode, accent) {
    const root = document.documentElement;
    const effective = mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode;
    root.classList.toggle("dark", effective === "dark");
    root.setAttribute("data-theme", effective);
    root.setAttribute("data-accent", accent);
    root.style.colorScheme = effective;
}

export function ThemeProvider({ children }) {
    const [mode, setModeState] = useState(() => {
        const stored = typeof window !== "undefined" ? localStorage.getItem("flow_theme_mode") : null;
        return MODES.includes(stored) ? stored : "light";
    });
    const [accent, setAccentState] = useState(() => {
        const stored = typeof window !== "undefined" ? localStorage.getItem("flow_theme_accent") : null;
        return ACCENTS.find(a => a.id === stored)?.id || "olive";
    });

    useEffect(() => { applyTheme(mode, accent); }, [mode, accent]);

    useEffect(() => {
        if (mode !== "system" || typeof window === "undefined") return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => applyTheme("system", accent);
        mq.addEventListener?.("change", handler);
        return () => mq.removeEventListener?.("change", handler);
    }, [mode, accent]);

    const setMode = useCallback((m) => {
        if (!MODES.includes(m)) return;
        setModeState(m);
        localStorage.setItem("flow_theme_mode", m);
    }, []);
    const setAccent = useCallback((a) => {
        if (!ACCENTS.find(x => x.id === a)) return;
        setAccentState(a);
        localStorage.setItem("flow_theme_accent", a);
    }, []);

    const effectiveMode = mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode;

    return (
        <ThemeCtx.Provider value={{ mode, accent, setMode, setAccent, effectiveMode, accents: ACCENTS, modes: MODES }}>
            {children}
        </ThemeCtx.Provider>
    );
}

export const useTheme = () => useContext(ThemeCtx);
